import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import QRCode from 'qrcode';
import { getItemById } from '@/services/itemService';
import type { Item } from '@packages/shared-types';

const PRINT_QR_BODY_CLASS = 'print-qr-active';

export function ItemPrintQrPage(): JSX.Element {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const [item, setItem] = useState<Item | null>(null);
  const [qrDataUrl, setQrDataUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    document.body.classList.add(PRINT_QR_BODY_CLASS);
    return () => document.body.classList.remove(PRINT_QR_BODY_CLASS);
  }, []);

  useEffect(() => {
    if (!id) return;
    getItemById(id)
      .then((data) => {
        setItem(data);
        const qrUrl = `${typeof window !== 'undefined' ? window.location.origin : 'https://app.trushelf.io'}/items/${data.id}`;
        return QRCode.toDataURL(qrUrl, { width: 256, margin: 2 });
      })
      .then(setQrDataUrl)
      .catch((err) => setError(err instanceof Error ? err.message : 'Failed to load'));
  }, [id]);

  useEffect(() => {
    if (item && qrDataUrl) {
      window.print();
    }
  }, [item, qrDataUrl]);

  if (error) {
    return (
      <div className="flex min-h-screen items-center justify-center p-8">
        <p className="text-destructive">{error}</p>
      </div>
    );
  }

  if (!item || !qrDataUrl) {
    return (
      <div className="flex min-h-screen items-center justify-center p-8">
        <p className="text-muted-foreground">{t('pages.items.list.loading')}</p>
      </div>
    );
  }

  return (
    <div className="print-qr-content min-h-screen bg-white p-4 print:p-4">
      <div className="mx-auto max-w-[320px] rounded border border-dashed border-gray-400 p-3">
        <div className="space-y-3">
          <div className="space-y-1 text-center">
            <h1 className="text-lg font-semibold text-foreground print:text-black">
              {item.name}
            </h1>
            {item.sku && (
              <p className="text-sm text-muted-foreground print:text-gray-700">
                {t('pages.items.form.sku')}: {item.sku}
              </p>
            )}
          </div>
          <div className="rounded-md border border-gray-200 bg-white px-2 pt-1.5 pb-1">
            <img
              src={qrDataUrl}
              alt="QR Code"
              className="mx-auto block"
              width={256}
              height={256}
            />
            <img
              src="/brand/trushelf-horizontal.svg"
              alt="TrueShelf"
              className="mx-auto mt-1 h-3.5 opacity-70"
            />
          </div>
        </div>
      </div>
    </div>
  );
}
